// @ts-nocheck
import { UserPlus, Search, Shield, Edit2, Trash2, MoreVertical, CheckCircle, XCircle, X, Upload, Loader2, AlertCircle, Key, Copy } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { useApi } from '../hooks/useApi'
import { useAuthStore } from '../stores/authStore'
import { formatPhoneNumberForDisplay, formatPhoneNumberForSave, formatPhoneNumberAsYouType } from '../utils/phoneNumberHelper'

export default function UsersPage() {
  const api = useApi()
  const { user, isAdmin } = useAuthStore((state) => ({
    user: state.user,
    isAdmin: state.isAdmin(),
  }))
  const currentUserId = user?.sub
  const [showAddModal, setShowAddModal] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const emptyForm = {
    userId: '',
    email: '',
    name: '',
    displayName: '',
    phoneNumber: '',
    role: 'operator',
    status: 'active',
    avatar: '',
    avatarFile: null,
  }
  const [formData, setFormData] = useState(emptyForm)
  const [avatarPreview, setAvatarPreview] = useState('')
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [lastCreatedInfo, setLastCreatedInfo] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [resetPasswordUser, setResetPasswordUser] = useState(null)
  const [resetPasswordInfo, setResetPasswordInfo] = useState(null)
  const [resettingPassword, setResettingPassword] = useState(false)

  // 一覧取得
  useEffect(() => {
    if (!user?.organizationId) return
    const fetchUsers = async () => {
      try {
        setLoading(true)
        const data = await api.get('/api/users')
        const items = data.items || []
        // 自分を先頭、そのほかはサーバー順を維持
        const reordered = items.sort((a, b) => {
          const aid = a.userId || a.id
          const bid = b.userId || b.id
          if (aid === currentUserId && bid !== currentUserId) return -1
          if (bid === currentUserId && aid !== currentUserId) return 1
          return 0
        })
        setUsers(reordered)
        setError(null)
      } catch (err) {
        console.error('ユーザー一覧取得エラー:', err)
        setError(err.message || 'ユーザー一覧の取得に失敗しました')
      } finally {
        setLoading(false)
      }
    }
    fetchUsers()
  }, [api, user?.organizationId])

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users
    const q = searchQuery.trim().toLowerCase()
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phoneNumber || '').includes(q)
    )
  }, [users, searchQuery])

  const roleLabels = {
    admin: '管理者',
    operator: 'オペレーター',
  }

  const roleColors = {
    admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    operator: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  }

  // アバター画像をアップロードして公開URLを取得
  const uploadAvatarFile = async (userId, file) => {
    try {
      console.log('アバターアップロード開始:', { userId, fileName: file.name, fileType: file.type, fileSize: file.size })
      
      const { uploadUrl, url } = await api.post(`/api/users/${userId}/avatar-url`, {
        contentType: file.type || 'image/png',
      })

      console.log('署名付きURL取得成功:', { uploadUrl: uploadUrl.substring(0, 100) + '...', publicUrl: url })

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'image/png',
        },
        body: file,
      })

      console.log('S3アップロード応答:', {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
        headers: Object.fromEntries(uploadResponse.headers.entries())
      })
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text()
        console.error('S3アップロードエラー:', { status: uploadResponse.status, errorText })
        throw new Error(`アバター画像のアップロードに失敗しました (${uploadResponse.status}): ${errorText || uploadResponse.statusText}`)
      }

      console.log('アバターアップロード成功:', url)
      return url
    } catch (error) {
      console.error('uploadAvatarFile エラー:', error)
      throw error
    }
  }

  const openCreateModal = () => {
    if (!isAdmin) {
      alert('ユーザーの作成は管理者のみ可能です')
      return
    }
    setModalMode('create')
    setFormData(emptyForm)
    setAvatarPreview('')
    setFormError('')
    setShowAddModal(true)
  }

  const openEditModal = (targetUser) => {
    const userId = targetUser.userId || targetUser.id
    if (!isAdmin && userId !== currentUserId) {
      alert('このユーザーを編集する権限がありません')
      return
    }
    setModalMode('edit')
    setFormData({
      userId,
      email: targetUser.email,
      name: targetUser.name || '',
      displayName: targetUser.displayName || '',
      phoneNumber: formatPhoneNumberForDisplay(targetUser.phoneNumber) || '',
      role: targetUser.role,
      status: targetUser.status,
      avatar: targetUser.avatar || '',
      avatarFile: null,
    })
    setAvatarPreview(targetUser.avatar || '')
    setFormError('')
    setShowAddModal(true)
  }

  const closeModal = () => {
    if (avatarPreview && avatarPreview.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreview)
    }
    setShowAddModal(false)
    setFormData(emptyForm)
    setAvatarPreview('')
    setFormError('')
    setSaving(false)
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    setFormData((prev) => {
      const next = { ...prev, avatarFile: file }
      if (avatarPreview && avatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview)
      }
      const preview = file ? URL.createObjectURL(file) : next.avatar || ''
      setAvatarPreview(preview)
      return next
    })
  }

  const handleRemoveAvatar = () => {
    if (avatarPreview && avatarPreview.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreview)
    }
    setFormData((prev) => ({
      ...prev,
      avatar: '',
      avatarFile: null,
    }))
    setAvatarPreview('')
  }

  const handleSaveUser = async () => {
    try {
      setSaving(true)
      setFormError('')

      if (modalMode === 'create' && !formData.email.trim()) {
        throw new Error('メールアドレスを入力してください')
      }
      if (!formData.displayName.trim()) {
        throw new Error('表示名を入力してください')
      }

      // 電話番号を国際形式（E.164）に変換
      const phoneNumberInternational = formData.phoneNumber.trim()
        ? formatPhoneNumberForSave(formData.phoneNumber.trim())
        : ''

      const payload = {
        name: formData.name.trim() || formData.displayName.trim(),
        displayName: formData.displayName.trim(),
        phoneNumber: phoneNumberInternational,
        role: formData.role,
        status: formData.status,
      }

      if (modalMode === 'create') {
        const createPayload = {
          email: formData.email.trim(),
          name: payload.name,
          displayName: payload.displayName,
          phoneNumber: payload.phoneNumber,
          role: payload.role,
        }
        const result = await api.post('/api/users', createPayload)
        let createdUser = result.user
        setLastCreatedInfo({
          email: result.user.email,
          temporaryPassword: result.password,
        })

        if (formData.avatarFile) {
          const avatarUrl = await uploadAvatarFile(result.user.userId, formData.avatarFile)
          const saved = await api.put(`/api/users/${result.user.userId}`, { avatar: avatarUrl })
          createdUser = { ...createdUser, ...saved }
          // キャッシュバスターを追加
          if (createdUser.avatar) {
            const separator = createdUser.avatar.includes('?') ? '&' : '?'
            createdUser.avatar = `${createdUser.avatar}${separator}t=${Date.now()}`
          }
        }

        setUsers((prev) => [createdUser, ...prev])
      } else {
        let avatarUrl = formData.avatar || null
        if (formData.avatarFile) {
          avatarUrl = await uploadAvatarFile(formData.userId, formData.avatarFile)
        }

        const updatePayload = {
          ...payload,
          avatar: avatarUrl,
        }

        const saved = await api.put(`/api/users/${formData.userId}`, updatePayload)

        // アバターURLにタイムスタンプを追加してキャッシュをバイパス
        const updatedUser = { ...saved }
        if (updatedUser.avatar && formData.avatarFile) {
          // 新しいアバターがアップロードされた場合、キャッシュバスターを追加
          const separator = updatedUser.avatar.includes('?') ? '&' : '?'
          updatedUser.avatar = `${updatedUser.avatar}${separator}t=${Date.now()}`
        }

        setUsers((prev) =>
          prev.map((u) =>
            (u.userId || u.id) === formData.userId
              ? { ...u, ...updatedUser }
              : u
          )
        )
      }

      closeModal()
    } catch (err) {
      console.error('ユーザー保存エラー:', err)
      setFormError(err.message || 'ユーザーの保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUser = async (targetUser) => {
    const userId = targetUser.userId || targetUser.id
    const confirmed = window.confirm(`ユーザー「${targetUser.displayName || targetUser.name || targetUser.email}」を削除しますか？`)
    if (!confirmed) return

    try {
      await api.delete(`/api/users/${userId}`)
      setUsers((prev) => prev.filter((u) => (u.userId || u.id) !== userId))
    } catch (err) {
      console.error('ユーザー削除エラー:', err)
      alert(err.message || 'ユーザーの削除に失敗しました')
    }
  }

  const openResetPasswordModal = (targetUser) => {
    const userId = targetUser.userId || targetUser.id
    if (!isAdmin && userId !== currentUserId) {
      alert('このユーザーのパスワードをリセットする権限がありません')
      return
    }
    setResetPasswordUser(targetUser)
    setResetPasswordInfo(null)
    setShowResetPasswordModal(true)
  }

  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false)
    setResetPasswordUser(null)
    setResetPasswordInfo(null)
    setResettingPassword(false)
  }

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return
    const userId = resetPasswordUser.userId || resetPasswordUser.id
    if (!isAdmin && userId !== currentUserId) {
      alert('このユーザーのパスワードをリセットする権限がありません')
      return
    }

    try {
      setResettingPassword(true)
      const result = await api.post(`/api/users/${userId}/reset-password`, {})
      
      setResetPasswordInfo({
        email: result.email,
        temporaryPassword: result.password,
      })
    } catch (err) {
      console.error('パスワードリセットエラー:', err)
      alert(err.message || 'パスワードのリセットに失敗しました')
    } finally {
      setResettingPassword(false)
    }
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      alert('クリップボードにコピーしました')
    } catch (err) {
      console.error('コピーエラー:', err)
      alert('コピーに失敗しました')
    }
  }

  return (
    <div className="space-y-6">
      {lastCreatedInfo && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                ユーザーを作成しました
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-200 mb-3">
                {lastCreatedInfo.email} の初回ログイン用一時パスワード:
              </p>
              <button
                onClick={() => copyToClipboard(lastCreatedInfo.temporaryPassword)}
                className="w-full bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-700 px-3 py-2 hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors group"
                title="クリックしてコピー"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-bold text-gray-900 dark:text-white break-all">
                    {lastCreatedInfo.temporaryPassword}
                  </span>
                  <Copy className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                </div>
              </button>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                クリックしてクリップボードにコピーできます
              </p>
            </div>
            <button
              onClick={() => setLastCreatedInfo(null)}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100 flex-shrink-0"
              aria-label="閉じる"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              総ユーザー数
            </p>
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {users.length}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              アクティブ
            </p>
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {users.filter(u => u.status === 'active').length}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              管理者
            </p>
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {users.filter(u => u.role === 'admin').length}
          </p>
        </div>
      </div>

      {/* ヘッダーとアクション */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* 検索 */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="名前やメールアドレスで検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* 追加ボタン */}
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span>ユーザー追加</span>
          </button>
        </div>
      </div>

      {/* ユーザーリスト */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-b-2 border-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300 font-medium">読み込み中...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
              ユーザー一覧の取得に失敗しました
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              ユーザーがいません
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              「ユーザー追加」ボタンから新しいオペレーターを追加してください
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              <span>最初のユーザーを追加</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    メールアドレス
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    表示名
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    電話番号
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    権限
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user.userId || user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.displayName || user.name || user.email}
                            className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-white">
                              {(user.displayName || user.name || user.email || '?')[0]}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.displayName || user.name || user.email}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {formatPhoneNumberForDisplay(user.phoneNumber) || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${roleColors[user.role]}`}>
                        <Shield className="w-3 h-3" />
                        {roleLabels[user.role]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.status === 'active' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          アクティブ
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
                          <XCircle className="w-3 h-3" />
                          無効
                        </span>
                      )}
                  </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {(isAdmin || user.userId === currentUserId || user.id === currentUserId) && (
                          <>
                            <button
                              onClick={() => openEditModal(user)}
                              className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="編集"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openResetPasswordModal(user)}
                              className="p-2 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                              title="パスワードリセット"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="削除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 追加/編集モーダル */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {modalMode === 'create' ? 'ユーザーを追加' : 'ユーザーを編集'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  表示名とアバターを含めてユーザー情報を設定できます
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {formError && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    メールアドレス（ログインID） <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="user@example.com"
                    disabled={modalMode === 'edit'}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    表示名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="コール担当A"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">電話番号</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => {
                      const formatted = formatPhoneNumberAsYouType(e.target.value)
                      setFormData({ ...formData, phoneNumber: formatted })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="090-1234-5678"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">権限</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="operator">オペレーター</option>
                    <option value="admin">管理者</option>
                  </select>
                </div>

                {modalMode === 'edit' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">ステータス</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="active">アクティブ</option>
                      <option value="inactive">無効</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">アバター</label>
                  <div className="flex items-center gap-3">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="avatar preview"
                        className="w-16 h-16 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400">
                        <UserPlus className="w-6 h-6" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-200 rounded-lg border border-blue-200 dark:border-blue-800 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50">
                        <Upload className="w-4 h-4" />
                        <span>画像を選択</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                      </label>
                      {avatarPreview && (
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          アバターを削除
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-70"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{modalMode === 'create' ? 'ユーザーを作成' : '更新して保存'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* パスワードリセットモーダル */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                  <Key className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    パスワードリセット
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {resetPasswordUser.displayName || resetPasswordUser.name || resetPasswordUser.email}
                  </p>
                </div>
              </div>
              <button
                onClick={closeResetPasswordModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {resetPasswordInfo ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-200 rounded-lg px-3 py-2">
                    <CheckCircle className="w-4 h-4 mt-0.5" />
                    <span>パスワードをリセットしました</span>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                      新しい一時パスワード
                    </p>
                    <button
                      onClick={() => copyToClipboard(resetPasswordInfo.temporaryPassword || resetPasswordInfo.password)}
                      className="w-full bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-700 p-3 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors group"
                      title="クリックしてコピー"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-lg font-mono font-bold text-gray-900 dark:text-white break-all">
                          {resetPasswordInfo.temporaryPassword || resetPasswordInfo.password}
                        </p>
                        <Copy className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      </div>
                    </button>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                      クリックしてクリップボードにコピーできます
                    </p>
                  </div>

                  <button
                    onClick={closeResetPasswordModal}
                    className="w-full px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    閉じる
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    このユーザーのパスワードを一時パスワードにリセットします。新しい一時パスワードが生成されます。
                  </p>

                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                    <p className="text-xs text-yellow-800 dark:text-yellow-200">
                      <strong>注意:</strong> このパスワードを忘れずに共有してください。
                    </p>
                  </div>

                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={closeResetPasswordModal}
                      disabled={resettingPassword}
                      className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white disabled:opacity-50"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={handleResetPassword}
                      disabled={resettingPassword}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-70"
                    >
                      {resettingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                      <span>パスワードをリセット</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
